import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
export function Testimonials() {
  const testimonials = [{
    quote: "I never thought my house could look this good. Blue Sky turned my outdated kitchen into something out of a magazine. My family can't stop talking about it.",
    name: 'Carmen R.',
    location: 'Philadelphia'
  }, {
    quote: "They finished my basement in record time and it looks absolutely incredible. My kids have their dream playroom and I have my home office. Best investment I've ever made.",
    name: 'Marcus T.',
    location: 'Montgomery County'
  }, {
    quote: "Professional, clean, on time, and the quality is outstanding. I've already recommended Blue Sky to three of my neighbors. They are simply the best.",
    name: 'Sandra M.',
    location: 'Delaware County'
  }, {
    quote: 'From the free estimate to the final walkthrough — the whole experience was smooth, transparent, and the results were beyond what I imagined.',
    name: 'Roberto G.',
    location: 'Bensalem PA'
  }];
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  }, [testimonials.length]);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);
  // Auto-advance every 6 seconds
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  const test = testimonials[current];
  return <section id="testimonials" className="py-28 bg-cream overflow-hidden" data-id="element-426">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-427">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-428">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-429">
            <div className="flex items-center justify-center gap-4 mb-6" data-id="element-430">
              <div className="h-px w-12 bg-gold" data-id="element-431"></div>
              <span className="text-gold font-bold uppercase tracking-widest text-xs" data-id="element-432">
                Client Stories
              </span>
              <div className="h-px w-12 bg-gold" data-id="element-433"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-navy mb-6" data-id="element-434">
              Real Homeowners. <br className="hidden md:block" data-id="element-435" />
              <span className="text-navy italic font-light" data-id="element-436">
                Real Transformations.
              </span>
            </h2>
          </motion.div>
        </div>

        {/* Carousel */}
        <div className="max-w-4xl mx-auto relative" data-id="element-437">
          {/* Navigation Arrows */}
          <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-16 z-10 w-12 h-12 rounded-full bg-white border border-gray-100 shadow-lg flex items-center justify-center text-navy hover:bg-navy hover:text-white hover:border-navy transition-all duration-300" aria-label="Previous testimonial" data-id="element-438">
            <ChevronLeft size={20} data-id="element-439" />
          </button>
          <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-16 z-10 w-12 h-12 rounded-full bg-white border border-gray-100 shadow-lg flex items-center justify-center text-navy hover:bg-navy hover:text-white hover:border-navy transition-all duration-300" aria-label="Next testimonial" data-id="element-440">
            <ChevronRight size={20} data-id="element-441" />
          </button>

          {/* Card */}
          <div className="min-h-[320px] md:min-h-[280px] flex items-center" data-id="element-442">
            <AnimatePresence mode="wait" custom={direction} data-id="element-443">
              <motion.div key={current} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1]
            }} className="w-full bg-white p-10 md:p-14 rounded-2xl shadow-[0_8px_40px_-8px_rgba(0,0,0,0.06)] relative border-l-4 border-gold" data-id="element-444">
                <Quote className="absolute top-8 right-8 md:top-10 md:right-12 w-20 h-20 text-cream" data-id="element-445" />

                <div className="flex gap-1 mb-6" data-id="element-446">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" data-id="element-447" />)}
                </div>

                <p className="text-lg md:text-xl text-navy mb-10 relative z-10 font-light leading-relaxed italic max-w-3xl" data-id="element-448">
                  "{test.quote}"
                </p>

                <div className="flex items-center gap-5" data-id="element-449">
                  <div className="w-14 h-14 bg-cream rounded-full flex items-center justify-center text-navy font-heading font-bold text-xl border border-gray-100" data-id="element-450">
                    {test.name.charAt(0)}
                  </div>
                  <div data-id="element-451">
                    <p className="font-bold text-navy tracking-wide" data-id="element-452">
                      {test.name}
                    </p>
                    <p className="text-xs text-muted uppercase tracking-widest mt-1" data-id="element-453">
                      {test.location}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-3 mt-10" data-id="element-454">
            {testimonials.map((_, index) => <button key={index} onClick={() => {
            setDirection(index > current ? 1 : -1);
            setCurrent(index);
          }} className={`transition-all duration-300 rounded-full ${index === current ? 'w-8 h-2 bg-gold' : 'w-2 h-2 bg-navy/20 hover:bg-navy/40'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-455" />)}
          </div>
        </div>
      </div>
    </section>;
}